/**
 * Authoritative Agency route authorization.
 *
 * Never grant Agency access from account_id / selected_account_id / pathname alone.
 * Canonical top-level auth/me fields win over stale active_account.type.
 */

export const VALID_AGENCY_ROLES = Object.freeze([
  'agency_owner',
  'agency_admin',
  'agency_manager',
  'agency_staff',
]);

export const SELF_REPAIR_ROLES = Object.freeze([
  'self_repair',
  'self_repair_customer',
  'self_repair_user',
  'premium_user',
]);

export const SELF_REPAIR_PORTALS = Object.freeze([
  'self_repair',
  'self_serve',
  'self_serve_portal',
]);

export const AGENCY_COMPATIBLE_PORTALS = Object.freeze([
  'agency',
  'agency_staff',
  'staff_portal',
]);

function norm(value) {
  return String(value || '').trim().toLowerCase();
}

/**
 * Resolve auth lifecycle for Agency guards.
 *
 * Critical: after /auth/me 401, loading=false and sessionReady=false.
 * That must be "unauthenticated", never permanent "loading".
 *
 * @returns {'bootstrapping'|'unauthenticated'|'authenticated'}
 */
export function resolveAgencyAuthPhase(authState, user = null) {
  if (typeof authState === 'string') {
    const phase = norm(authState);
    if (phase === 'loading' || phase === 'pending') return 'bootstrapping';
    if (phase === 'unauthenticated') return 'unauthenticated';
    if (phase === 'authenticated' || phase === 'account_selection_required') {
      return 'authenticated';
    }
  }

  const state = (authState && typeof authState === 'object') ? authState : {};
  const authStatus = norm(state.authState);
  const bootstrap = norm(state.bootstrapState);

  // Pending bootstrap wins over the initial authState default of "unauthenticated".
  // AuthContext starts as authState=unauthenticated + loading=true + bootstrap=pending.
  if (state.loading === true || bootstrap === 'pending') {
    return 'bootstrapping';
  }

  if (
    authStatus === 'unauthenticated'
    || bootstrap === 'unauthenticated'
    || bootstrap === 'error'
  ) {
    return 'unauthenticated';
  }

  if (
    authStatus === 'authenticated'
    || authStatus === 'account_selection_required'
  ) {
    // Authenticated but session shell not ready yet.
    if (state.sessionReady === false && state.loading !== false) {
      return 'bootstrapping';
    }
    if (state.sessionReady === false && !user) {
      return 'bootstrapping';
    }
    return 'authenticated';
  }

  // Explicit: bootstrap finished with no user (e.g. 401: loading=false, sessionReady=false).
  if (state.loading === false && !user) {
    return 'unauthenticated';
  }

  // Legacy callers pass { sessionReady: true } once ready.
  if (state.sessionReady === true && state.loading !== true) {
    return user ? 'authenticated' : 'unauthenticated';
  }

  // Legacy: sessionReady false without authState — only bootstrapping while loading≠false.
  if (state.sessionReady === false && state.loading !== false) {
    return 'bootstrapping';
  }

  if (state.loading === false) {
    return user ? 'authenticated' : 'unauthenticated';
  }

  return 'bootstrapping';
}

/**
 * Canonical fields only — do not prefer stale active_account.type for Self Repair.
 */
export function readCanonicalAgencyScope(user) {
  if (!user) {
    return {
      role: null,
      accountType: null,
      portalType: null,
      agencyId: null,
    };
  }

  const role = norm(
    user.effective_role
    || user.role
    || user.active_account?.effective_role
    || user.active_account?.role
  );

  const topAccountType = norm(user.account_type);
  const topPortal = norm(user.portal || user.portal_type);
  const nestedPortal = norm(user.active_account?.portal);
  const portalType = topPortal || nestedPortal;

  // agency_id only — never account_id / selected_account_id.
  const agencyIdRaw = user.agency_id;
  const agencyId = agencyIdRaw && agencyIdRaw !== 'default_agency'
    ? String(agencyIdRaw)
    : null;

  // Top-level Self Repair wins over nested stale type=agency / account_id.
  if (
    SELF_REPAIR_ROLES.includes(role)
    || topAccountType === 'self_repair'
    || SELF_REPAIR_PORTALS.includes(portalType)
  ) {
    return {
      role: SELF_REPAIR_ROLES.includes(role) ? role : 'self_repair',
      accountType: 'self_repair',
      portalType: SELF_REPAIR_PORTALS.includes(portalType) ? portalType : 'self_repair',
      agencyId: null,
    };
  }

  const nestedType = norm(
    user.active_account?.account_type
    || user.active_account?.type
  );

  return {
    role,
    accountType: topAccountType || nestedType || null,
    portalType,
    agencyId,
  };
}

export function isCanonicalSelfRepairScope(user) {
  const scope = readCanonicalAgencyScope(user);
  return (
    scope.accountType === 'self_repair'
    || SELF_REPAIR_PORTALS.includes(scope.portalType)
    || SELF_REPAIR_ROLES.includes(scope.role)
  );
}

export function isCanonicalAgencyScope(user) {
  const scope = readCanonicalAgencyScope(user);
  if (isCanonicalSelfRepairScope(user)) return false;
  return (
    scope.accountType === 'agency'
    && AGENCY_COMPATIBLE_PORTALS.includes(scope.portalType)
    && Boolean(scope.agencyId)
    && VALID_AGENCY_ROLES.includes(scope.role)
  );
}

/**
 * @param {object|null} user
 * @param {{ loading?: boolean, sessionReady?: boolean, authState?: string, bootstrapState?: string }|string} authState
 * @param {string} [pathname]
 * @param {{ agencyId?: string, supportContext?: object|null, isInSupportMode?: boolean }} [opts]
 * @returns {{ state: 'loading'|'allow'|'redirect'|'deny', agencyId?: string, to?: string, reason?: string }}
 */
export function resolveAgencyRouteAccess(user, authState, pathname = '', opts = {}) {
  // Backward-compatible call shape used by older tests:
  // resolveAgencyRouteAccess({ user, agencyId, supportContext, isInSupportMode })
  if (user && typeof user === 'object' && 'user' in user && authState === undefined) {
    const legacy = user;
    return resolveAgencyRouteAccess(
      legacy.user,
      {
        loading: false,
        sessionReady: true,
        authState: legacy.user ? 'authenticated' : 'unauthenticated',
      },
      pathname || `/agency/${legacy.agencyId || ''}`,
      {
        agencyId: legacy.agencyId,
        supportContext: legacy.supportContext,
        isInSupportMode: legacy.isInSupportMode,
      }
    );
  }

  const phase = resolveAgencyAuthPhase(authState, user);

  // Unauthenticated check MUST run before account/portal/agency/support classification.
  // Also must run before treating sessionReady=false as loading (post-401 trap).
  if (phase === 'unauthenticated') {
    return {
      state: 'redirect',
      to: '/login',
      reason: 'unauthenticated',
      allow: false,
      redirectTo: '/login',
    };
  }

  if (phase === 'bootstrapping') {
    return { state: 'loading', allow: false };
  }

  if (!user) {
    return {
      state: 'redirect',
      to: '/login',
      reason: 'unauthenticated',
      allow: false,
      redirectTo: '/login',
    };
  }

  const routeAgencyId = opts.agencyId
    || (String(pathname || '').match(/^\/agency\/([^/]+)/) || [])[1]
    || null;

  // 1) Self Repair — fail closed before any Agency / support allow path.
  if (isCanonicalSelfRepairScope(user)) {
    return {
      state: 'redirect',
      to: '/dashboard',
      reason: 'self_repair_scope',
      allow: false,
      redirectTo: '/dashboard',
    };
  }

  // Support-context Self Repair (even if actor shell still looks like platform).
  const support = opts.supportContext;
  if (opts.isInSupportMode && support) {
    const supportPortal = norm(support.effective_portal_type || support.portal_type);
    const supportRole = norm(support.effective_role || support.role);
    const supportAccountType = norm(support.account_type);
    if (
      SELF_REPAIR_PORTALS.includes(supportPortal)
      || SELF_REPAIR_ROLES.includes(supportRole)
      || supportAccountType === 'self_repair'
    ) {
      return {
        state: 'redirect',
        to: support.redirect_path || '/dashboard',
        reason: 'self_repair_scope',
        allow: false,
        redirectTo: support.redirect_path || '/dashboard',
      };
    }
  }

  // 2) Canonical Agency membership — all four requirements.
  if (isCanonicalAgencyScope(user)) {
    const scope = readCanonicalAgencyScope(user);
    if (routeAgencyId && String(routeAgencyId) !== String(scope.agencyId)) {
      return {
        state: 'redirect',
        to: `/agency/${scope.agencyId}/dashboard`,
        reason: 'wrong_agency',
        allow: false,
        redirectTo: `/agency/${scope.agencyId}/dashboard`,
      };
    }
    return {
      state: 'allow',
      agencyId: scope.agencyId,
      reason: 'agency_membership',
      allow: true,
    };
  }

  // 3) Platform Enter Agency support — actor is platform AND support has a real
  //    agency_id with Agency-compatible portal. Never if Self Repair above.
  if (opts.isInSupportMode && support) {
    const supportPortal = norm(support.effective_portal_type || support.portal_type);
    const supportAgencyId = support.effective_agency_id || support.agency_id || null;
    const supportAccountType = norm(support.account_type);
    const supportRole = norm(support.effective_role || support.role);
    const actorRole = norm(user.role || user.effective_role);
    const actorIsPlatform = (
      user.is_platform_admin === true
      || user.platform_access === true
      || ['platform_owner', 'super_admin', 'platform_admin'].includes(actorRole)
      || norm(user.account_type) === 'platform'
      || norm(user.portal) === 'platform'
      // After /auth/me Agency overlay, actor shell keeps platform_owner role while
      // account_type becomes agency — still treat as platform Enter Agency actor.
      || (
        Boolean(user.real_actor_user_id || user.is_support_mode)
        && ['platform_owner', 'super_admin'].includes(norm(user.role))
      )
    );

    const supportIsAgency = (
      supportAgencyId
      && (
        AGENCY_COMPATIBLE_PORTALS.includes(supportPortal)
        || supportAccountType === 'agency'
        || VALID_AGENCY_ROLES.includes(supportRole)
      )
    );

    if (actorIsPlatform && supportIsAgency) {
      if (routeAgencyId && String(routeAgencyId) !== String(supportAgencyId)) {
        return {
          state: 'redirect',
          to: `/agency/${supportAgencyId}/dashboard`,
          reason: 'support_wrong_agency',
          allow: false,
          redirectTo: `/agency/${supportAgencyId}/dashboard`,
        };
      }
      return {
        state: 'allow',
        agencyId: supportAgencyId,
        reason: 'platform_enter_agency_support',
        allow: true,
      };
    }

    if (supportPortal === 'managed_client_portal') {
      return {
        state: 'redirect',
        to: '/client/dashboard',
        reason: 'support_managed_client',
        allow: false,
        redirectTo: '/client/dashboard',
      };
    }
  }

  // 4) Platform without Agency support context.
  const actorRole = norm(user.role || user.effective_role);
  if (
    user.is_platform_admin === true
    || user.platform_access === true
    || ['platform_owner', 'super_admin', 'platform_admin'].includes(actorRole)
    || norm(user.account_type) === 'platform'
    || norm(user.portal) === 'platform'
  ) {
    return {
      state: 'redirect',
      to: '/platform/dashboard',
      reason: 'platform_scope',
      allow: false,
      redirectTo: '/platform/dashboard',
    };
  }

  return {
    state: 'deny',
    reason: 'invalid_agency_scope',
    to: '/dashboard',
    allow: false,
    redirectTo: '/dashboard',
  };
}

/** @deprecated Prefer resolveAgencyRouteAccess state machine. */
export function canEnterAgencyForMembership(accountOrUser) {
  if (!accountOrUser) return false;
  if (isCanonicalSelfRepairScope(accountOrUser)) return false;
  const role = norm(accountOrUser.effective_role || accountOrUser.role);
  if (!VALID_AGENCY_ROLES.includes(role) && !['owner', 'admin', 'staff', 'specialist'].includes(role)) {
    return false;
  }
  const accountType = norm(accountOrUser.account_type || accountOrUser.type);
  const portal = norm(accountOrUser.portal || accountOrUser.portal_type);
  if (accountType === 'self_repair' || SELF_REPAIR_PORTALS.includes(portal)) {
    return false;
  }
  return accountType === 'agency'
    || accountType === 'subaccount'
    || portal === 'agency'
    || portal === 'agency_staff'
    || Boolean(accountOrUser.agency_id);
}
